// src/pages/RefundPolicyPage.jsx
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

export default function RefundPolicyPage() {
  const navigate  = useNavigate();
  const { user }  = useAuth();

  const lastUpdated = "May 26, 2025";
  const email       = "support@smartaicareer.com";
  const appName     = "ResumeScoreAI";
  const domain      = "smartaicareer.com";
  const price       = "$4.99";

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", fontFamily: "'Inter', -apple-system, sans-serif" }}>

      {/* NAV */}
      <nav style={{ borderBottom: "1px solid #e2e8f0", padding: "0 5%", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fff", position: "sticky", top: 0, zIndex: 100 }}>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
          <div style={{ width: 32, height: 32, background: "linear-gradient(135deg, #2563eb, #1d4ed8)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: "#fff", fontSize: 16, fontWeight: 700 }}>R</span>
          </div>
          <span style={{ fontWeight: 800, fontSize: 18, color: "#0f172a", letterSpacing: "-0.03em" }}>
            ResumeScore<span style={{ color: "#2563eb" }}>AI</span>
          </span>
        </Link>
        <button
          onClick={() => navigate(user ? "/dashboard" : "/")}
          style={{ padding: "8px 18px", background: "transparent", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 8, fontWeight: 500, fontSize: 14, cursor: "pointer" }}
        >
          ← Back
        </button>
      </nav>

      {/* CONTENT */}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "48px 5% 80px" }}>

        {/* Header */}
        <div style={{ marginBottom: 40 }}>
          <div style={{ display: "inline-block", background: "#eff6ff", color: "#2563eb", fontSize: 12, fontWeight: 600, padding: "4px 12px", borderRadius: 20, marginBottom: 16, border: "1px solid #bfdbfe" }}>
            Legal
          </div>
          <h1 style={{ fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 800, color: "#0f172a", marginBottom: 8, letterSpacing: "-0.02em" }}>
            Refund Policy
          </h1>
          <p style={{ fontSize: 14, color: "#64748b" }}>Last updated: {lastUpdated}</p>
        </div>

        {/* Quick summary box */}
        <div style={{ background: "#f0fdf4", borderLeft: "4px solid #059669", padding: "16px 20px", borderRadius: 8, marginBottom: 36, fontSize: 14, color: "#065f46", lineHeight: 1.7 }}>
          <strong>The short version:</strong> If something went wrong on our end, we will refund you immediately — no questions asked. If you received your analysis and results, payments are generally final. Email us at <a href={`mailto:${email}`} style={{ color: "#059669" }}>{email}</a> and we will sort it out within 48 hours.
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 36 }}>

          <Section title="1. Our Commitment">
            <p>
              {appName} charges a one-time fee of {price} per analysis. We want every customer to feel the purchase was worthwhile. If you have a problem with your order, we will always try to make it right.
            </p>
            <p>
              Because our service delivers digital results instantly, refunds are evaluated based on whether the service was actually delivered. We do not offer blanket refunds for change of mind, but we are generous in cases where something went wrong.
            </p>
          </Section>

          <Section title="2. When You Are Entitled to a Full Refund">
            <p>You are entitled to a full refund in any of the following situations:</p>
            <ul>
              <li>The analysis failed to complete due to a technical error on our side</li>
              <li>You were charged but never received your results</li>
              <li>You were charged twice for the same order</li>
              <li>The service was unavailable or severely degraded at the time of your purchase</li>
              <li>You received a clearly incomplete or corrupted result</li>
            </ul>
            <p>In these cases, email us within <strong>7 days</strong> of purchase with your transaction ID and we will process a full refund within 48 hours.</p>
          </Section>

          <Section title="3. When Refunds Are Not Available">
            <p>We do not offer refunds in the following situations:</p>
            <ul>
              <li>You received your ATS analysis</li>
              <li>Your resume score was lower than you expected — scores reflect the actual keyword match and are not guaranteed to be high</li>
              <li>You changed your mind after the analysis was delivered</li>
              <li>You disagree with the AI recommendations — results are guidance, not guarantees</li>
              <li>More than 7 days have passed since the purchase</li>
            </ul>
          </Section>

          <Section title="4. Free Resume Generator">
            <p>
              Each paid analysis unlocks a free AI-generated resume. Once you have generated the resume, this portion of the service has been delivered and is non-refundable.
            </p>
            <p>
              If the resume generation failed due to a technical error, we will either regenerate it or issue a partial refund at our discretion.
            </p>
          </Section>

          <Section title="5. How to Request a Refund">
            <p>To request a refund, email us at <a href={`mailto:${email}`}>{email}</a> with:</p>
            <ul>
              <li>Your account email address</li>
              <li>Your transaction ID (found in your payment confirmation email or in the Account section of the dashboard)</li>
              <li>A brief description of what went wrong</li>
            </ul>
            <p>
              We will respond within <strong>48 hours</strong> on business days. If approved, the refund will appear on your original payment method within <strong>5–10 business days</strong> depending on your bank.
            </p>
          </Section>

          <Section title="6. Chargebacks">
            <p>
              We strongly encourage you to contact us before disputing a charge with your bank. Most issues can be resolved quickly and a chargeback creates unnecessary friction for both parties.
            </p>
            <p>
              If a chargeback is filed without contacting us first, we reserve the right to provide evidence to your bank that the service was delivered as described.
            </p>
          </Section>

          <Section title="7. Contact Us">
            <p>If you have any questions about this Refund Policy, please reach out:</p>
            <div style={{ background: "#f8fafc", borderRadius: 10, padding: "16px 20px", marginTop: 12, border: "1px solid #e2e8f0" }}>
              <p style={{ margin: "4px 0", fontSize: 14, color: "#374151" }}><strong>{appName}</strong></p>
              <p style={{ margin: "4px 0", fontSize: 14, color: "#374151" }}>
                📧 <a href={`mailto:${email}`} style={{ color: "#2563eb" }}>{email}</a>
              </p>
              <p style={{ margin: "4px 0", fontSize: 14, color: "#374151" }}>
                🌐 <a href={`https://${domain}`} style={{ color: "#2563eb" }}>{domain}</a>
              </p>
              <p style={{ margin: "8px 0 4px", fontSize: 13, color: "#64748b" }}>
                Response time: within 48 hours on business days
              </p>
            </div>
          </Section>

        </div>

        {/* Footer nav */}
        <div style={{ marginTop: 48, paddingTop: 24, borderTop: "1px solid #e2e8f0", display: "flex", gap: 16, flexWrap: "wrap" }}>
          <Link to="/terms"   style={{ fontSize: 14, color: "#2563eb", textDecoration: "none", fontWeight: 500 }}>Terms of Service →</Link>
          <Link to="/privacy" style={{ fontSize: 14, color: "#2563eb", textDecoration: "none", fontWeight: 500 }}>Privacy Policy →</Link>
          <Link to="/support" style={{ fontSize: 14, color: "#2563eb", textDecoration: "none", fontWeight: 500 }}>Contact Support →</Link>
        </div>
      </div>

      {/* FOOTER */}
      <footer style={{ borderTop: "1px solid #e2e8f0", padding: "16px 5%", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fff", flexWrap: "wrap", gap: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>ResumeScore<span style={{ color: "#2563eb" }}>AI</span></span>
        <span style={{ fontSize: 13, color: "#94a3b8" }}>© {new Date().getFullYear()} ResumeScoreAI. All rights reserved.</span>
      </footer>
    </div>
  );
}

// ── Reusable section component — matches TermsOfServicePage style ──
function Section({ title, children }) {
  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid #f1f5f9", letterSpacing: "-0.01em" }}>
        {title}
      </h2>
      <div style={{ fontSize: 14, color: "#475569", lineHeight: 1.8 }}>
        {children}
      </div>
      <style>{`
        div p  { margin: 0 0 12px 0; }
        div ul { margin: 8px 0 12px 0; padding-left: 20px; }
        div ul li { margin-bottom: 6px; }
        div a  { color: #2563eb; }
        div a:hover { text-decoration: underline; }
      `}</style>
    </div>
  );
}
